import React, { useEffect, useMemo } from "react";
import { Alert, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { WhatsAppTemplate } from "../../models/whatsapp.model";
import { TemplateConfig, TemplateVariableMapping, TemplateComponent } from "../../services/campaign.service";
import { getTemplateBodyVariableNames } from "../../utilities/whatsappTemplate.utility";

type Props = {
  template?: WhatsAppTemplate | null;
  availableColumns: string[];
  value: TemplateVariableMapping[];
  onChange: (value: TemplateVariableMapping[]) => void;
  title?: string;
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

export function mappingsFromTemplateConfig(template?: TemplateConfig | null): TemplateVariableMapping[] {
  if (template?.variableMappings?.length) return template.variableMappings;
  const body = template?.components?.find((component) => component.type === "body");
  return (body?.parameters || []).map((parameter, index) => ({
    parameterName: String(index + 1),
    source: "STATIC",
    value: parameter.text || "",
  }));
}

export function componentsFromVariableMappings(mappings: TemplateVariableMapping[]): TemplateComponent[] {
  if (!mappings.length) return [];
  return [{
    type: "body",
    parameters: mappings.map((mapping) => {
      const parameterName = mapping.parameterName.trim();
      return {
        type: "text",
        text: mapping.source === "STATIC" ? mapping.value || "" : mapping.fallback || "",
        ...(parameterName && !/^\d+$/.test(parameterName)
          ? { parameter_name: parameterName }
          : {}),
      };
    }),
  }];
}

export default function TemplateVariableMapper({ template, availableColumns, value, onChange, title = "Template variables" }: Props) {
  const parameterNames = useMemo(() => getTemplateBodyVariableNames(template), [template]);
  const parameterKey = parameterNames.join("|");
  const columnKey = availableColumns.join("|");

  useEffect(() => {
    const next = parameterNames.map((parameterName, index) => {
      const existing = value.find((mapping) => mapping.parameterName === parameterName) || value[index];
      if (existing) return { ...existing, parameterName };
      const matchingColumn = availableColumns.find((column) => normalize(column) === normalize(parameterName));
      return {
        parameterName,
        source: matchingColumn ? "COLUMN" as const : "STATIC" as const,
        column: matchingColumn || "",
        value: "",
        fallback: "",
      };
    });
    if (JSON.stringify(next) !== JSON.stringify(value)) onChange(next);
    // Reconcile only when the selected template or available contact schema changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterKey, columnKey]);

  if (!template) return null;

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      {!parameterNames.length ? (
        <Alert severity="info">This template does not contain body variables.</Alert>
      ) : (
        <>
          {value.map((mapping, index) => (
            <Paper key={`${mapping.parameterName}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                <Typography fontWeight={700} sx={{ minWidth: 180 }}>{`{{${mapping.parameterName}}}`}</Typography>
                <FormControl fullWidth>
                  <InputLabel>Value source</InputLabel>
                  <Select
                    label="Value source"
                    value={mapping.source === "STATIC" ? "__STATIC__" : mapping.column || ""}
                    onChange={(event) => {
                      const selected = event.target.value;
                      onChange(value.map((item, itemIndex) => itemIndex === index ? {
                        ...item,
                        source: selected === "__STATIC__" ? "STATIC" : "COLUMN",
                        column: selected === "__STATIC__" ? "" : selected,
                      } : item));
                    }}
                  >
                    <MenuItem value="__STATIC__">Fixed value</MenuItem>
                    {availableColumns.map((column) => (
                      <MenuItem key={column} value={column}>
                        {column === "name" ? "Contact name" : column === "phone" ? "WhatsApp number" : column}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={mapping.source === "STATIC" ? "Fixed value" : "Fallback if empty"}
                  value={mapping.source === "STATIC" ? mapping.value || "" : mapping.fallback || ""}
                  onChange={(event) => onChange(value.map((item, itemIndex) => itemIndex === index
                    ? mapping.source === "STATIC"
                      ? { ...item, value: event.target.value }
                      : { ...item, fallback: event.target.value }
                    : item))}
                  helperText={mapping.source === "COLUMN" ? "Optional" : undefined}
                  fullWidth
                />
              </Stack>
            </Paper>
          ))}
        </>
      )}
    </Stack>
  );
}
